import { useEffect, useRef, useState } from 'react';

export default function Gauge({
  value,
  max = 100,
  label,
  size = 200,
  color = 'var(--c-sage)',
  valueFormat,
}: {
  value: number;
  max?: number;
  label?: string;
  size?: number;
  color?: string;
  valueFormat?: (v: number) => string;
}) {
  const [anim, setAnim] = useState(0);
  const raf = useRef(0);
  useEffect(() => {
    const from = anim;
    const to = value;
    const t0 = performance.now();
    const dur = 900;
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnim(from + (to - from) * eased);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);

  const r = size / 2 - 14;
  const cx = size / 2;
  const cy = size / 2;
  const frac = Math.min(1, Math.max(0, anim / max));
  const startAng = Math.PI * 0.8;
  const endAng = startAng + Math.PI * 1.4 * frac;
  const arcFull = describeArc(cx, cy, r, startAng, startAng + Math.PI * 1.4);
  const arc = describeArc(cx, cy, r, startAng, endAng);
  return (
    <svg width={size} height={size * 0.72} viewBox={`0 0 ${size} ${size * 0.72}`}>
      <path d={arcFull} stroke="var(--c-border)" strokeWidth="12" fill="none" strokeLinecap="round" />
      <path d={arc} stroke={color} strokeWidth="12" fill="none" strokeLinecap="round" />
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="28" fontWeight="700" fill="var(--c-text)">
        {valueFormat ? valueFormat(anim) : Math.round(anim)}
      </text>
      {label && (
        <text x={cx} y={cy + 18} textAnchor="middle" fontSize="12" fill="var(--c-muted)">
          {label}
        </text>
      )}
    </svg>
  );
}

function describeArc(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const p0 = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
  const p1 = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${p0[0]} ${p0[1]} A ${r} ${r} 0 ${large} 1 ${p1[0]} ${p1[1]}`;
}
